import { webApi } from "../webApi.ts";
import { agentFomoDomain, apiUrlPath } from "../constants.ts";

const getZodDef = (schema: unknown): Record<string, unknown> | undefined => {
  if (typeof schema !== "object" || schema === null) return undefined;
  return ((schema as Record<string, unknown>)._def ||
    (schema as Record<string, unknown>).def) as
      | Record<string, unknown>
      | undefined;
};

const getTypeName = (def: Record<string, unknown>): string => {
  const t = (def.typeName || def.type) as string;
  if (!t) return "any";
  return t.toLowerCase().replace(/^zod/, "");
};

const isZodOptional = (schema: unknown): boolean => {
  const def = getZodDef(schema);
  if (!def) return false;
  const type = getTypeName(def);
  if (type === "optional") return true;
  if (type === "effects") return isZodOptional(def.schema);
  if (type === "default") return isZodOptional(def.innerType);
  return false;
};

const isZodNullable = (schema: unknown): boolean => {
  const def = getZodDef(schema);
  if (!def) return false;
  const type = getTypeName(def);
  if (type === "nullable") return true;
  if (type === "effects") return isZodNullable(def.schema);
  if (type === "default") return isZodNullable(def.innerType);
  return false;
};

const unwrapZodSchema = (schema: unknown): unknown => {
  const def = getZodDef(schema);
  if (!def) return schema;
  const type = getTypeName(def);
  if (type === "optional" || type === "nullable") {
    return unwrapZodSchema((schema as { unwrap: () => unknown }).unwrap());
  }
  if (type === "effects") {
    return unwrapZodSchema(def.schema);
  }
  if (type === "default") {
    return unwrapZodSchema(def.innerType);
  }
  return schema;
};

const getArrayElement = (
  def: Record<string, unknown>,
  schema: unknown,
): unknown => {
  return def.element || def.type || (schema as { element?: unknown }).element;
};

const getEnumValues = (
  def: Record<string, unknown>,
  schema: unknown,
): unknown[] => {
  if (def.entries) {
    return Object.keys(def.entries);
  }
  if (def.values) {
    return def.values as unknown[];
  }
  if (schema && typeof schema === "object" && "options" in schema) {
    return (schema as { options: unknown[] }).options;
  }
  return [];
};

const getUnionOptions = (
  def: Record<string, unknown>,
  schema: unknown,
): unknown[] => {
  return (def.options as unknown[]) ||
    (schema as { options?: unknown[] }).options || [];
};

const getZodTypeName = (schema: unknown): string => {
  const unwrapped = unwrapZodSchema(schema);
  const def = getZodDef(unwrapped);
  if (!def) return "any";

  const type = getTypeName(def);
  if (type === "string") return "string";
  if (type === "number") return "number";
  if (type === "boolean") return "boolean";
  if (type === "enum") {
    const values = getEnumValues(def, unwrapped);
    return values.map((v) => `"${v}"`).join(" | ");
  }
  if (type === "array") {
    const element = getArrayElement(def, unwrapped);
    return `${getZodTypeName(element)}[]`;
  }
  if (type === "object") return "object";
  if (type === "union") {
    const options = getUnionOptions(def, unwrapped);
    return options.map(getZodTypeName).join(" | ");
  }
  return "any";
};

export const renderZodSchema = (schema: unknown, indent = ""): string => {
  const unwrapped = unwrapZodSchema(schema);
  const def = getZodDef(unwrapped);
  if (!def) return "any";

  const type = getTypeName(def);

  if (type === "object") {
    const lines: string[] = [];
    const shape = (unwrapped as { shape?: Record<string, unknown> }).shape ||
      (def.shape as Record<string, unknown>);
    if (shape) {
      for (const [key, val] of Object.entries(shape)) {
        const isOptional = isZodOptional(val);
        const isNullable = isZodNullable(val);
        const inner = unwrapZodSchema(val);
        const innerDef = getZodDef(inner);
        if (!innerDef) continue;

        const innerType = getTypeName(innerDef);
        const typeName = getZodTypeName(inner);
        const desc = (inner as { description?: string }).description
          ? ` - ${(inner as { description: string }).description}`
          : "";

        const optStr = isOptional ? "optional" : "";
        const nullStr = isNullable ? "nullable" : "";
        const flags = [optStr, nullStr].filter(Boolean).join(", ");
        const flagPrefix = flags ? ` (${flags})` : "";

        if (innerType === "object") {
          lines.push(
            `${indent}- **${key}** (object${flagPrefix})${desc}:`,
          );
          lines.push(renderZodSchema(inner, indent + "  "));
        } else {
          lines.push(
            `${indent}- **${key}** (${typeName}${flagPrefix})${desc}`,
          );
        }
      }
    }
    return lines.join("\n");
  }

  if (type === "array") {
    const element = unwrapZodSchema(getArrayElement(def, unwrapped));
    const elementDef = getZodDef(element);
    if (elementDef && getTypeName(elementDef) === "object") {
      return `${indent}Array of objects:\n${
        renderZodSchema(element, indent + "  ")
      }`;
    }
    return `${indent}Array of ${getZodTypeName(element)}`;
  }

  return `${indent}${getZodTypeName(unwrapped)}`;
};

export const generateApiDocsMarkdown = (): string => {
  const sections: string[] = [];

  sections.push(`# Agent FOMO Developer API Documentation

Welcome to the Agent FOMO public developer API! 
Our API enables you to search cultural events, check user profiles, find ticket trades, and integrate FOMO search directly into your own tools, client-side apps, or AI assistants.

## Server URL
- Base URL: \`https://${agentFomoDomain}${apiUrlPath}\`
- Method: \`POST\`
- Content-Type: \`application/json\`

## Authentication
Every request to the API must include an \`Authorization\` header with a valid token:
\`\`\`http
Authorization: Bearer <YOUR_API_TOKEN>
\`\`\`

You can obtain a personalized API token directly from the Agent FOMO assistant by asking "Give me an API token" or "Generate an API token for me".

## Request Format
To call any endpoint, make a \`POST\` request to \`https://${agentFomoDomain}${apiUrlPath}\` with a JSON body specifying the \`endpoint\` and \`payload\`:

\`\`\`json
{
  "endpoint": "getEvents",
  "payload": {
    "time": {
      "startDatetime": "oct-10 14:00"
    },
    "limit": 10
  }
}
\`\`\`
`);

  sections.push(`## Partner Event Submission Webhooks

Beyond the token-authed search API above, Agent FOMO accepts secure, real-time event submissions from third-party partners. There are two submission endpoints, both authenticated via HMAC signatures (separate from the Bearer token used for the search API).

### Security & Secret Setup
Requests are authenticated with a shared secret key (\`SUBMIT_WEBHOOK_SECRET\`) used to compute and verify **HMAC-SHA256** signatures. Generate a secure random string and send it to us securely beforehand so we can configure it in our environment. Do not share it with anyone else.

### Computing the Signature
Sign the **entire raw request body** with the shared secret and send it in the \`x-signature\` header on every request:

\`\`\`javascript
const crypto = require("crypto");

function computeSignature(secret, rawBody) {
  return crypto
    .createHmac("sha256", secret)
    .update(rawBody, "utf8")
    .digest("hex"); // lowercase hex string
}
\`\`\`

### Endpoint: Structured Direct Ingestion (\`POST /submit-structured\`)
Use this if you have fully-structured, rich event data. It bypasses AI parsing, geocodes the address, matches categories, and inserts the event directly.

- URL: \`https://${agentFomoDomain}/submit-structured\`
- Method: \`POST\`
- Headers: \`Content-Type: application/json\`, \`x-signature: <computed_signature_hex>\`

Required fields: \`id\` (number, unique tracing/dedupe id), \`name\` (string), \`startDate\` (ISO-8601 UTC), \`endDate\` (ISO-8601 UTC), \`description\` (string), \`address\` (string, geocoded automatically), \`eventLink\` (string).

Optional fields include \`type\` (array of category strings), \`minAge\` (number), \`sex\`, \`tickets\` (array; used to derive price ranges), \`organizerInfo\`, \`mediaFiles\`, \`creator\`, and other display/booking flags. Unknown fields are stored on the raw payload.

### Endpoint: Unstructured Text Submission (\`POST /submit\`)
Use this if you only have raw text, chats, or unformatted flyers. Our AI parsing engine extracts name, date, time, and location.

- URL: \`https://${agentFomoDomain}/submit\`
- Method: \`POST\`
- Headers: \`Content-Type: application/json\`, \`x-signature: <computed_signature_hex>\`

Required fields: \`text\` (string, the raw content), \`source\` (string identifying the submitter), \`timezoneIana\` (string, e.g. \`Asia/Jerusalem\`).

### Webhook Response Statuses
- \`200 OK\` — payload verified, mapped, and queued or stored.
- \`400 Bad Request\` — malformed JSON or invalid parameter values.
- \`401 Unauthorized\` — missing \`x-signature\` header or invalid HMAC signature.
- \`500 Internal Error\` — unexpected server error while processing.`);

  sections.push(`## Endpoints`);

  for (const [name, endpoint] of Object.entries(webApi)) {
    if (name === "getApiToken") continue;
    sections.push(`### \`${name}\`

#### Request Payload Schema
${renderZodSchema(endpoint.input)}

#### Response Schema
${renderZodSchema(endpoint.output)}
`);
  }

  return sections.join("\n\n");
};
